import { useQuery } from '@tanstack/react-query';
import { LayoutGrid, List, Network } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EntityCombobox } from '@/components/shared/entity-combobox';
import { FormField } from '@/components/shared/form-field';
import { PageHeader } from '@/components/shared/page-header';
import { EmptyState, ErrorState } from '@/components/shared/states';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { api } from '@/lib/api';
import { PeopleSubnav } from '@/pages/people/people-subnav';

import { FamilyTreeDisplay } from './family-tree-display';
import {
  displayPersonName,
  familyLinkCount,
  type FamilyTreeData,
  type FamilyView,
} from './family-tree-types';
import { type MemberRecord } from './member-form-state';

const INITIAL_DEPTH = 6;

export const FamiliesPage = () => {
  const { t } = useTranslation('people');
  const [memberId, setMemberId] = useState<string | null>(null);
  const [memberQuery, setMemberQuery] = useState('');
  const [view, setView] = useState<FamilyView>('tree');
  const [depth, setDepth] = useState(INITIAL_DEPTH);

  useEffect(() => {
    setDepth(INITIAL_DEPTH);
  }, [memberId]);

  const peopleSearch = useQuery({
    queryKey: ['people', 'members', 'families-picker', memberQuery],
    queryFn: () =>
      api.get<{ data: MemberRecord[] }>(
        `/people/members?${new URLSearchParams({
          page: '1',
          pageSize: '20',
          ...(memberQuery ? { q: memberQuery } : {}),
        }).toString()}`,
      ),
  });

  const selectedMember = useQuery({
    queryKey: ['people', 'member', memberId],
    queryFn: () => api.get<MemberRecord>(`/people/members/${memberId}`),
    enabled: Boolean(memberId),
  });

  const familyTree = useQuery({
    queryKey: ['people', 'member', memberId, 'family-tree', depth],
    queryFn: () =>
      api.get<FamilyTreeData>(`/people/members/${memberId}/family/tree`, {
        query: { depth },
      }),
    enabled: Boolean(memberId),
  });

  const memberOptions = useMemo(
    () =>
      (peopleSearch.data?.data ?? []).map((row) => ({
        id: row.id,
        label: displayPersonName(row),
        description: row.phone ?? undefined,
      })),
    [peopleSearch.data?.data],
  );

  const selectedOption = useMemo(() => {
    const row = selectedMember.data;
    if (!row) return null;
    return {
      id: row.id,
      label: displayPersonName(row),
      description: row.phone ?? undefined,
    };
  }, [selectedMember.data]);

  const centerPerson = selectedMember.data
    ? {
        id: selectedMember.data.id,
        firstName: selectedMember.data.firstName,
        middleName: selectedMember.data.middleName,
        lastName: selectedMember.data.lastName,
        gender: selectedMember.data.gender,
        status: selectedMember.data.status,
        photoUrl: selectedMember.data.photoUrl,
      }
    : null;

  const hasLinks = familyTree.data ? familyLinkCount(familyTree.data) > 0 : false;
  const showTree = Boolean(memberId && centerPerson && familyTree.data && !familyTree.isError);

  return (
    <div>
      <PageHeader
        title={t('families.title')}
        description={t('families.description')}
        tipId="members.families.purpose"
      />
      <PeopleSubnav />

      <Card className="mb-6">
        <CardContent className="space-y-4 pt-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <FormField label={t('families.person')} className="min-w-0 flex-1 lg:max-w-md">
              {(field) => (
                <EntityCombobox
                  id={field.id}
                  value={memberId}
                  onChange={setMemberId}
                  options={memberOptions}
                  selected={selectedOption}
                  onQueryChange={setMemberQuery}
                  loading={peopleSearch.isLoading}
                  placeholder={t('families.personPlaceholder')}
                  emptyMessage={t('families.personEmpty')}
                  clearLabel={t('families.clearPerson')}
                />
              )}
            </FormField>

            {memberId ? (
              <div
                className="flex rounded-lg border border-border p-0.5"
                role="group"
                aria-label={t('families.view.label')}
              >
                {(
                  [
                    { value: 'tree', icon: Network, label: t('families.view.tree') },
                    { value: 'grid', icon: LayoutGrid, label: t('families.view.grid') },
                    { value: 'list', icon: List, label: t('families.view.list') },
                  ] as const
                ).map((item) => (
                  <Button
                    key={item.value}
                    type="button"
                    size="sm"
                    variant={view === item.value ? 'primary' : 'ghost'}
                    aria-pressed={view === item.value}
                    aria-label={item.label}
                    onClick={() => setView(item.value)}
                  >
                    <item.icon className="size-4" aria-hidden />
                  </Button>
                ))}
              </div>
            ) : null}
          </div>

          {!memberId ? (
            <EmptyState
              title={t('families.emptySelection.title')}
              body={t('families.emptySelection.description')}
            />
          ) : selectedMember.isLoading || familyTree.isLoading ? (
            <div className="flex justify-center py-16">
              <Spinner />
            </div>
          ) : selectedMember.isError || familyTree.isError ? (
            <ErrorState
              title={t('families.error.title')}
              body={t('families.error.description')}
              onRetry={() => {
                void selectedMember.refetch();
                void familyTree.refetch();
              }}
            />
          ) : showTree && centerPerson && familyTree.data ? (
            hasLinks ? (
              <div className="space-y-4">
                <p className="text-body-sm text-fg-secondary">
                  {t('families.depthSummary', {
                    count: familyTree.data.relativeCount,
                    depth: familyTree.data.exploredDepth,
                  })}
                </p>
                <FamilyTreeDisplay centerPerson={centerPerson} tree={familyTree.data} view={view} />
                {familyTree.data.hasMore && familyTree.data.nextDepth ? (
                  <div className="flex justify-center">
                    <Button
                      type="button"
                      variant="outline"
                      loading={familyTree.isFetching}
                      onClick={() => setDepth(familyTree.data!.nextDepth!)}
                    >
                      {t('families.loadMore', { depth: familyTree.data.nextDepth })}
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : (
              <EmptyState
                title={t('families.noLinks.title', { name: displayPersonName(centerPerson) })}
                body={t('families.noLinks.description')}
              />
            )
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};
